import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaceIcon from '@mui/icons-material/Place';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import NewsQueryBuilderDialog, {
  newsQueryHasTerms,
  parseNewsQuery,
  NewsQuery,
} from './NewsQueryBuilderDialog';
import AddMemberDialog, { AddMemberDraft } from './AddMemberDialog';
import MonitorInfoDialog from './MonitorInfoDialog';
import IntelPacketSentinel from './IntelPacketSentinel';
import MissionWorkspace from '@signals/app-shell/features/missions/MissionWorkspace';
import { sequentialWaypointLabel } from '@signals/app-shell/features/routePlanner/io/waypointLabel';
import SubjectDossierPanel from '@signals/app-shell/features/subjects/SubjectDossierPanel';
import {
  addEvidence,
  exportPacket,
  addPacketNote,
  deleteEvidence,
  collectNow,
  createAssessment,
  createInsight,
  getPacket,
  importNotes,
  IntelActivityEvent,
  IntelActivityPlayer,
  IntelAssessment,
  IntelAuditEntry,
  IntelEvidence,
  IntelInsight,
  IntelMonitor,
  IntelPacket,
  IntelMember,
  IntelPacketNote,
  MemberType,
  listActivity,
  listActivityObservations,
  listAssessments,
  listAudit,
  listEntities,
  listEvidence,
  fetchFieldReportPhoto,
  listInsights,
  listPacketNotes,
  listUnfiledNotes,
  getPacketMapLayers,
  PacketMapLayers,
  PacketMapPoint,
  PacketMapPolygon,
  promoteMember,
  removeMember,
  runHistoricPol,
  updateMember,
  formatPacketMonitorLine,
  MONITOR_LABELS,
  WIRED_MONITOR_KINDS,
  MonitorKind,
  PACKET_STATUS_COLOR,
  PACKET_STATUS_HELP,
  PACKET_STATUS_LABELS,
  PacketStatus,
  nextStatuses,
  normalizePacketStatus,
  patchMonitors,
  statusAllowsCollect,
  PUBLISH_FROM_STATUSES,
  research,
  sentinelAssessment,
  sentinelInsights,
  UnfiledNote,
  updatePacket,
  validateInsight,
} from '@signals/app-shell/services/intelPacketApi';

interface IntelPacketWorkspaceProps {
  packetId: number;
  onBack: () => void;
  onZoomTo?: (lat: number, lng: number, zoom?: number) => void;
  onLoadPacketLayer?: (layerId: string, points: PacketMapPoint[], line?: GeoJSON.LineString | null) => void;
  onLoadPacketGeofences?: (packetId: number, fences: PacketMapPolygon[]) => void;
  onClearPacketLayers?: (packetId: number) => void;
  onWaypointPickChange?: (active: boolean) => void;
  waypointPickRef?: React.MutableRefObject<((longitude: number, latitude: number) => void) | null>;
}

type MainTab = 'overview' | 'assessment' | 'analysis' | 'map' | 'research' | 'evidence' | 'subjects' | 'entities' | 'mission' | 'admin';
type AnalysisSub = 'activity' | 'pol' | 'insights';
type AdminSub = 'monitoring' | 'audit' | 'access';

const BUFFER_KINDS = new Set<MonitorKind>(['adtech', 'dfp', 'news']);
const BUFFER_MAX_M = 2500;

const ENTITY_TYPE_LABELS: Record<string, string> = {
  geofence: 'Geofence',
  area: 'Area',
  device: 'Device',
  watchlist: 'Watchlist',
  adtech_id: 'AdTech',
  overwatch_article: 'OSINT',
  dfp_alert: 'DFP',
  person: 'Person',
  field_report: 'Field report',
};

function memberNotes(m: IntelMember): string {
  const notes = m.meta && (m.meta as { notes?: unknown }).notes;
  return typeof notes === 'string' ? notes : '';
}

function memberExcluded(m: IntelMember): boolean {
  return !!(m.meta && (m.meta as { excluded?: boolean }).excluded);
}

function memberOsint(m: IntelMember): Record<string, any> | null {
  const raw = m.meta && (m.meta as { osint?: Record<string, any> }).osint;
  return raw && typeof raw === 'object' ? raw : null;
}

const EntityRosterCard: React.FC<{
  member: IntelMember;
  busy: boolean;
  onSave: (patch: { label?: string; notes?: string; excluded?: boolean }) => Promise<void>;
  onRemove: () => Promise<void>;
  onHistoric?: () => Promise<void>;
}> = ({ member, busy, onSave, onRemove, onHistoric }) => {
  const [name, setName] = useState(member.label || member.member_id);
  const [notes, setNotes] = useState(memberNotes(member));
  const excluded = memberExcluded(member);
  const osint = memberOsint(member);

  useEffect(() => {
    setName(member.label || member.member_id);
    setNotes(memberNotes(member));
  }, [member]);

  return (
    <Box sx={{ mb: 1, p: 1, border: '1px solid', borderColor: excluded ? 'rgba(255,255,255,0.12)' : 'divider', borderRadius: 1, opacity: excluded ? 0.75 : 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {ENTITY_TYPE_LABELS[member.member_type] || member.member_type}
        {member.is_primary ? ' · primary' : ''}
        {excluded ? ' · excluded' : ''}
      </Typography>
      <TextField
        size="small"
        fullWidth
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => {
          const next = name.trim();
          if (next && next !== (member.label || member.member_id)) onSave({ label: next });
        }}
        sx={{ mt: 0.75, mb: 0.75 }}
      />
      <Typography variant="caption" sx={{ display: 'block', fontFamily: 'ui-monospace, Menlo, monospace', mb: 0.75, wordBreak: 'break-all' }}>
        {member.member_id}
      </Typography>
      <TextField
        size="small"
        fullWidth
        multiline
        minRows={2}
        label="Notes / descriptor"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={() => {
          if (notes !== memberNotes(member)) onSave({ notes });
        }}
      />
      {(member.member_type === 'device' || member.member_type === 'adtech_id' || member.member_type === 'watchlist') && !excluded && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {(member.meta as { scope?: string } | null)?.scope === 'aoi'
            ? 'PoL stays inside the packet fence.'
            : 'PoL uses this ID everywhere, not only inside the packet fence.'}
        </Typography>
      )}
      {!!(member.meta as { pol_snapshot?: { lookback_days?: number; places?: unknown[]; new_aois?: unknown[] } } | null)?.pol_snapshot && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Historic {(member.meta as any).pol_snapshot.lookback_days}d · {((member.meta as any).pol_snapshot.places || []).length} places · {((member.meta as any).pol_snapshot.new_aois || []).length} new AOIs
        </Typography>
      )}
      {member.member_type === 'overwatch_article' && osint && (
        <Box sx={{ mt: 0.75 }}>
          {!!osint.url && (
            <Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-all' }}>
              <a href={osint.url} target="_blank" rel="noreferrer">{osint.source || osint.url}</a>
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {[
              osint.is_translated ? 'translated' : osint.language,
              osint.sentiment,
              osint.primary_category,
              osint.published_at ? new Date(osint.published_at).toLocaleDateString() : '',
            ].filter(Boolean).join(' · ')}
          </Typography>
          {!!osint.summary && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap' }}>
              {osint.summary}
            </Typography>
          )}
          {[
            { label: 'Orgs', values: (osint.organizations || []).slice(0, 8) },
            { label: 'People', values: (osint.persons || []).slice(0, 6) },
            { label: 'Themes', values: (osint.themes || []).slice(0, 6) },
          ].filter((g) => g.values.length > 0).map((g) => (
            <Typography key={g.label} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
              {g.label}: {g.values.join(', ')}
            </Typography>
          ))}
        </Box>
      )}
      <Box sx={{ mt: 0.5 }}>
        {onHistoric && !excluded && (member.member_type === 'device' || member.member_type === 'adtech_id' || member.member_type === 'watchlist') && (
          <Button size="small" disabled={busy} onClick={() => onHistoric()}>
            Re-run historic
          </Button>
        )}
        {!member.is_primary && (
          <Button size="small" disabled={busy} onClick={() => onSave({ excluded: !excluded })}>
            {excluded ? 'Watch again' : 'Exclude'}
          </Button>
        )}
        {!member.is_primary && (
          <Button size="small" color="inherit" disabled={busy} onClick={onRemove}>
            Remove
          </Button>
        )}
      </Box>
    </Box>
  );
};

const IntelPacketWorkspace: React.FC<IntelPacketWorkspaceProps> = ({ packetId, onBack, onZoomTo, onLoadPacketLayer, onLoadPacketGeofences, onClearPacketLayers, onWaypointPickChange, waypointPickRef }) => {
  const [packet, setPacket] = useState<IntelPacket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<MainTab>('overview');
  const [analysisSub, setAnalysisSub] = useState<AnalysisSub>('activity');
  const [adminSub, setAdminSub] = useState<AdminSub>('monitoring');

  const [activity, setActivity] = useState<IntelActivityEvent[]>([]);
  const [insights, setInsights] = useState<IntelInsight[]>([]);
  const [assessments, setAssessments] = useState<IntelAssessment[]>([]);
  const [notes, setNotes] = useState<IntelPacketNote[]>([]);
  const [evidence, setEvidence] = useState<IntelEvidence[]>([]);
  const [audit, setAudit] = useState<IntelAuditEntry[]>([]);
  const [entities, setEntities] = useState<{ primary: any; by_type: Record<string, any[]> } | null>(null);
  const [unfiled, setUnfiled] = useState<UnfiledNote[]>([]);
  const [selectedUnfiled, setSelectedUnfiled] = useState<Set<number>>(new Set());
  const [obsOpen, setObsOpen] = useState<number | null>(null);
  const [obsSummary, setObsSummary] = useState('');
  const [obsPlayers, setObsPlayers] = useState<IntelActivityPlayer[]>([]);
  const [addingPlayer, setAddingPlayer] = useState<string | null>(null);
  const focusedPacket = useRef<number | null>(null);

  const [noteDraft, setNoteDraft] = useState('');
  const [insightTitle, setInsightTitle] = useState('');
  const [insightFinding, setInsightFinding] = useState('');
  const [assessLabel, setAssessLabel] = useState('Initial');
  const [assessSummary, setAssessSummary] = useState('');
  const [assessFindings, setAssessFindings] = useState('');
  const [assessPol, setAssessPol] = useState('');
  const [assessGaps, setAssessGaps] = useState('');
  const [researchQuery, setResearchQuery] = useState('');
  const [researchResult, setResearchResult] = useState<{ summary: string; sources: any[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [collectNote, setCollectNote] = useState<string | null>(null);
  const [bufferDraft, setBufferDraft] = useState<Partial<Record<MonitorKind, number>>>({});
  const [newsQueryOpen, setNewsQueryOpen] = useState(false);
  const [monitorInfoKind, setMonitorInfoKind] = useState<MonitorKind | null>(null);
  const [addDraft, setAddDraft] = useState<AddMemberDraft | null>(null);
  const [mapLayers, setMapLayers] = useState<PacketMapLayers | null>(null);
  const [sentinelBusy, setSentinelBusy] = useState<'insights' | 'assessment' | 'ask' | null>(null);
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);

  const loadHeader = useCallback(async () => {
    const p = await getPacket(packetId);
    setPacket(p);
    return p;
  }, [packetId]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, act, ins, ass, nts, ev, au, ent] = await Promise.all([
        getPacket(packetId),
        listActivity(packetId),
        listInsights(packetId),
        listAssessments(packetId),
        listPacketNotes(packetId),
        listEvidence(packetId),
        listAudit(packetId),
        listEntities(packetId),
      ]);
      setPacket(p);
      setActivity(act);
      setInsights(ins);
      setAssessments(ass);
      setNotes(nts);
      setEvidence(ev);
      setAudit(au);
      setEntities(ent);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load packet');
    } finally {
      setLoading(false);
    }
  }, [packetId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    focusedPacket.current = null;
  }, [packetId]);

  useEffect(() => {
    if (!onZoomTo || !packet?.focus || focusedPacket.current === packet.id) return;
    const lat = Number(packet.focus.latitude);
    const lng = Number(packet.focus.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    focusedPacket.current = packet.id;
    onZoomTo(lat, lng, packet.focus.zoom);
  }, [packet, onZoomTo]);

  const loadLayerRef = useRef(onLoadPacketLayer);
  const loadFencesRef = useRef(onLoadPacketGeofences);
  const clearLayersRef = useRef(onClearPacketLayers);
  loadLayerRef.current = onLoadPacketLayer;
  loadFencesRef.current = onLoadPacketGeofences;
  clearLayersRef.current = onClearPacketLayers;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const layers = await getPacketMapLayers(packetId);
        if (cancelled) return;
        setMapLayers(layers);
        const load = loadLayerRef.current;
        if (!load) return;
        load(`intel-packet-${packetId}-roster`, layers.roster || []);
        load(`intel-packet-${packetId}-evidence`, layers.evidence || []);
        load(`intel-packet-${packetId}-activity`, layers.activity || []);
        load(`intel-packet-${packetId}-pol`, layers.pol || []);
        load(`intel-packet-${packetId}-insights`, layers.insights || []);
        loadFencesRef.current?.(packetId, layers.geofences || []);
      } catch {
        /* map overlay is best-effort */
      }
    })();
    return () => { cancelled = true; };
  }, [packetId, activity, insights]);

  useEffect(() => () => {
    clearLayersRef.current?.(packetId);
  }, [packetId]);

  const latestAssessment = useMemo(() => {
    const published = assessments.find((a) => a.published_at);
    return published || assessments[0] || packet?.latest_assessment || null;
  }, [assessments, packet]);

  const enabledMonitors = (packet?.monitors || []).filter((m) => m.enabled);
  const infoMonitor = (packet?.monitors || []).find((x) => x.kind === monitorInfoKind) || null;

  const memberKeys = useMemo(() => {
    const keys = new Set<string>();
    (packet?.members || []).forEach((m) => {
      if (m.member_id) keys.add(m.member_id);
      if (m.label) keys.add(m.label);
    });
    return keys;
  }, [packet]);

  const excludedKeys = useMemo(() => {
    const keys = new Set<string>();
    (packet?.members || []).forEach((m) => {
      if (!m.meta || !(m.meta as { excluded?: boolean }).excluded) return;
      if (m.member_id) keys.add(m.member_id);
      if (m.label) keys.add(m.label);
    });
    return keys;
  }, [packet]);

  const refreshRoster = async (next?: IntelPacket) => {
    if (next) setPacket(next);
    else setPacket(await getPacket(packetId));
    setEntities(await listEntities(packetId));
  };

  const playerMemberType = (p: IntelActivityPlayer): MemberType | null => {
    if (p.kind === 'adtech') return 'adtech_id';
    if (p.kind === 'article') return 'overwatch_article';
    if (p.kind === 'device' || p.kind === 'entity') return 'device';
    return null;
  };

  const addPlayerToPacket = async (p: IntelActivityPlayer) => {
    const memberType = playerMemberType(p);
    if (!memberType || !p.id) return;
    if (memberKeys.has(p.id) && !excludedKeys.has(p.id)) {
      setTab('entities');
      return;
    }
    if (memberType === 'overwatch_article') {
      setAddingPlayer(p.id);
      try {
        await refreshRoster(await promoteMember(packetId, {
          member_type: memberType,
          member_id: p.id,
          label: p.label || p.id,
          meta: { excluded: false },
        }));
      } finally {
        setAddingPlayer(null);
      }
      return;
    }
    setAddDraft({
      member_type: memberType,
      member_id: p.id,
      label: p.label || p.id,
      notes: '',
      scope: 'global',
      lookback_days: 30,
    });
  };

  const confirmAddMember = async (draft: AddMemberDraft) => {
    setAddingPlayer(draft.member_id);
    try {
      const next = await promoteMember(packetId, {
        member_type: draft.member_type,
        member_id: draft.member_id,
        label: draft.label,
        meta: {
          excluded: false,
          notes: draft.notes,
          scope: draft.scope,
          lookback_days: draft.lookback_days,
        },
      });
      const added = (next.members || []).find(
        (m) => m.member_type === draft.member_type && m.member_id === draft.member_id,
      );
      if (added) {
        const hist = await runHistoricPol(packetId, added.id, {
          lookback_days: draft.lookback_days,
          scope: draft.scope,
        });
        await refreshRoster(hist.packet);
        setActivity(await listActivity(packetId));
        setInsights(await listInsights(packetId));
        setTab('analysis');
        setAnalysisSub('pol');
        const pt = hist.snapshot?.new_aois?.[0] || hist.snapshot?.places?.[0];
        if (pt && onZoomTo && Number.isFinite(pt.latitude) && Number.isFinite(pt.longitude)) {
          onZoomTo(pt.latitude, pt.longitude, 14);
        }
      } else {
        await refreshRoster(next);
      }
      setAddDraft(null);
      setTab('entities');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Add failed');
    } finally {
      setAddingPlayer(null);
    }
  };

  const excludePlayer = async (p: IntelActivityPlayer) => {
    const memberType = playerMemberType(p);
    if (!memberType || !p.id) return;
    setAddingPlayer(p.id);
    try {
      const next = await promoteMember(packetId, {
        member_type: memberType,
        member_id: p.id,
        label: p.label || p.id,
        meta: { excluded: true },
      });
      await refreshRoster(next);
    } finally {
      setAddingPlayer(null);
    }
  };

  const summarizeCollect = (collected: Record<string, number>) => {
    const parts = Object.entries(collected)
      .filter(([, n]) => n > 0)
      .map(([kind, n]) => `${MONITOR_LABELS[kind as MonitorKind] || kind} ${n}`);
    return parts.length ? `Collected ${parts.join(', ')}` : 'No new observations';
  };

  const handleStatus = async (status: PacketStatus) => {
    if (!packet || status === packet.status) return;
    setBusy(true);
    setError(null);
    try {
      const next = await updatePacket(packetId, { status });
      setPacket(next);
      if (statusAllowsCollect(status) && !statusAllowsCollect(packet.status)) {
        setCollectNote('Collection tasked. Enabled monitors are pulling on the server.');
        await loadAll();
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Status change failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCollect = async () => {
    if (!statusAllowsCollect(packet?.status)) {
      setError('Collect is off in Draft/Closed. Move to Collection to task sources.');
      return;
    }
    setBusy(true);
    setCollectNote(null);
    try {
      const res = await collectNow(packetId);
      setCollectNote(summarizeCollect(res.collected || {}));
      await loadAll();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Collect failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSentinelInsights = async () => {
    setSentinelBusy('insights');
    setError(null);
    try {
      const res = await sentinelInsights(packetId);
      setInsights(await listInsights(packetId));
      setTab('analysis');
      setAnalysisSub('insights');
      setCollectNote(
        res.insights?.length
          ? `Sentinel drafted ${res.insights.length} insight${res.insights.length === 1 ? '' : 's'} — accept or reject each.`
          : 'Sentinel had nothing new to add. Collect more, then try again.',
      );
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Sentinel insights failed');
    } finally {
      setSentinelBusy(null);
    }
  };

  const handleSentinelAssessment = async () => {
    setSentinelBusy('assessment');
    setError(null);
    try {
      const res = await sentinelAssessment(packetId);
      const draft = res.draft;
      setAssessSummary(draft.executive_summary || '');
      setAssessFindings(draft.key_findings || '');
      setAssessPol(draft.pol_assessment || '');
      setAssessGaps(draft.gaps || '');
      setTab('assessment');
      setComposeOpen(true);
      setCollectNote('Sentinel drafted an assessment. Review it, then publish when you are ready.');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Sentinel assessment failed');
    } finally {
      setSentinelBusy(null);
    }
  };

  const toggleMonitor = async (m: IntelMonitor) => {
    setBusy(true);
    try {
      const turningOn = !m.enabled;
      const patch: Array<{ kind: MonitorKind; enabled: boolean }> = [
        { kind: m.kind as MonitorKind, enabled: turningOn },
      ];
      if (m.kind === 'pol') {
        patch.push({ kind: 'recurrence', enabled: false });
      }
      const monitors = await patchMonitors(packetId, patch);
      setPacket((prev) => (prev ? { ...prev, monitors } : prev));
      if (turningOn && statusAllowsCollect(packet?.status)) {
        const res = await collectNow(packetId);
        setCollectNote(summarizeCollect(res.collected || {}));
        await loadAll();
      } else if (turningOn) {
        setCollectNote('Monitor armed. Move status to Collection to start pulling.');
      }
    } finally {
      setBusy(false);
    }
  };

  const monitorBuffer = (m: IntelMonitor) => {
    if (bufferDraft[m.kind] != null) return bufferDraft[m.kind] as number;
    const raw = m.thresholds?.buffer_meters;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? Math.max(0, Math.min(BUFFER_MAX_M, n)) : 0;
  };

  const saveMonitorThresholds = async (m: IntelMonitor, patch: Record<string, unknown>) => {
    const next = { ...(m.thresholds || {}), ...patch };
    const monitors = await patchMonitors(packetId, [{ kind: m.kind as MonitorKind, thresholds: next }]);
    setPacket((prev) => (prev ? { ...prev, monitors } : prev));
  };

  const commitBuffer = async (m: IntelMonitor, meters: number) => {
    await saveMonitorThresholds(m, { buffer_meters: meters });
    setBufferDraft((prev) => {
      const next = { ...prev };
      delete next[m.kind];
      return next;
    });
  };

  const applyNewsQuery = async (query: NewsQuery) => {
    const news = (packet?.monitors || []).find((m) => m.kind === 'news');
    if (news) {
      await saveMonitorThresholds(news, {
        news_query: newsQueryHasTerms(query) ? query : {},
      });
    }
    setNewsQueryOpen(false);
  };

  const formatBuffer = (meters: number) => (
    meters >= 1000 ? `${(meters / 1000).toFixed(meters % 1000 === 0 ? 0 : 1)} km` : `${meters} m`
  );

  const openObservations = async (eventId: number) => {
    if (obsOpen === eventId) {
      setObsOpen(null);
      return;
    }
    const res = await listActivityObservations(packetId, eventId);
    setObsSummary(res.summary || '');
    setObsPlayers(res.players || []);
    setObsOpen(eventId);
  };

  if (loading && !packet) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!packet) {
    return (
      <Box sx={{ p: 1.5 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} size="small">Back</Button>
        <Typography color="error" variant="body2">{error || 'Packet not found'}</Typography>
      </Box>
    );
  }

  const packetStatus = normalizePacketStatus(packet.status);
  const canCollect = statusAllowsCollect(packetStatus);
  const canPublish = PUBLISH_FROM_STATUSES.includes(packetStatus);

  const subjectLabel = packet.primary_subject_type
    ? `${packet.primary_subject_type.replace(/_/g, ' ')}${packet.primary_subject_id ? ` · ${packet.primary_subject_id}` : ''}`
    : 'No primary subject';
  const draftInsights = insights.filter((i) => i.validation === 'draft' && i.origin !== 'analyst').length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{
        px: 1.5, pt: 1.25, pb: 1,
        background: 'linear-gradient(180deg, rgba(245,185,66,0.08) 0%, transparent 100%)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, flex: '1 1 220px', minWidth: 0 }}>
            <IconButton size="small" onClick={onBack} sx={{ mt: -0.15 }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle2"
                title={packet.name}
                sx={{ fontWeight: 800, letterSpacing: 0.15, lineHeight: 1.25, overflowWrap: 'anywhere' }}
              >
                {packet.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
              >
                {subjectLabel}
                {packet.assigned_analyst ? ` · ${packet.assigned_analyst}` : ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15, ml: 'auto', flexShrink: 0 }}>
            <Tooltip title={PACKET_STATUS_HELP[packetStatus]}>
              <Box
                onClick={(e) => setStatusAnchor(e.currentTarget)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.6, px: 0.5, py: 0.25,
                  cursor: 'pointer', borderRadius: 0.75,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: PACKET_STATUS_COLOR[packetStatus] }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: PACKET_STATUS_COLOR[packetStatus] }}>
                  {PACKET_STATUS_LABELS[packetStatus]}
                </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Assessment">
              <IconButton size="small" onClick={() => setTab('assessment')} sx={{ color: '#f5b942' }}>
                <AutoAwesomeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export packet report">
              <span>
                <IconButton
                  size="small"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setError(null);
                    try {
                      await exportPacket(packetId, 'html');
                    } catch (e: any) {
                      setError(e?.response?.data?.detail || e?.message || 'Export failed');
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  <FileDownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canCollect ? 'Run enabled monitors' : 'Collect is off until status is Collection or later'}>
              <span>
                <IconButton size="small" onClick={handleCollect} disabled={busy || !canCollect}>
                  {busy ? <CircularProgress size={16} /> : <PlayArrowIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={loadAll}><RefreshIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4.5, mt: 0.25 }}>
          {formatPacketMonitorLine(packet)}
          {enabledMonitors.length === 0 ? ' — arm collectors in Administration' : ''}
        </Typography>
      </Box>
      <Menu anchorEl={statusAnchor} open={!!statusAnchor} onClose={() => setStatusAnchor(null)}>
        <Typography variant="caption" sx={{ display: 'block', px: 2, py: 0.75, color: 'text.secondary', maxWidth: 260 }}>
          {PACKET_STATUS_HELP[packetStatus]}
        </Typography>
        <Divider />
        {nextStatuses(packetStatus).map((s) => (
          <MenuItem
            key={s}
            dense
            onClick={() => { setStatusAnchor(null); handleStatus(s); }}
          >
            Move to {PACKET_STATUS_LABELS[s]}
          </MenuItem>
        ))}
        {nextStatuses(packetStatus).length === 0 && (
          <MenuItem dense disabled>No further transitions</MenuItem>
        )}
      </Menu>
      {collectNote && (
        <Typography variant="caption" sx={{ px: 1.5, color: '#f5b942' }}>{collectNote}</Typography>
      )}
      {error && <Typography variant="caption" color="error" sx={{ px: 1.5 }}>{error}</Typography>}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 36, px: 1,
          '& .MuiTab-root': {
            minHeight: 36, minWidth: 'auto', px: 1.1, py: 0,
            fontSize: '0.72rem', textTransform: 'none', fontWeight: 600,
          },
          '& .Mui-selected': { color: '#f5b942' },
          '& .MuiTabs-indicator': { backgroundColor: '#f5b942' },
        }}
      >
        <Tab value="overview" label="Overview" />
        <Tab value="assessment" label="Assessment" />
        <Tab value="analysis" label="Analysis" />
        <Tab value="map" label="Map" />
        <Tab value="research" label="Research" />
        <Tab value="evidence" label="Evidence" />
        <Tab value="subjects" label="Subjects" />
        <Tab value="entities" label="Entities" />
        <Tab value="mission" label="Mission" />
        <Tab value="admin" label="Admin" />
      </Tabs>
      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1.5 }}>
        {tab === 'overview' && (
          <Box>
            <IntelPacketSentinel
              packet={packet}
              busy={sentinelBusy}
              onBusy={setSentinelBusy}
              onError={setError}
            />

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 0.75, flexWrap: 'wrap' }}>
              {[
                { label: 'activity', value: activity.length, go: () => { setTab('analysis'); setAnalysisSub('activity'); } },
                { label: draftInsights ? `insights · ${draftInsights} draft` : 'insights', value: insights.length, go: () => { setTab('analysis'); setAnalysisSub('insights'); } },
                { label: 'evidence', value: evidence.length, go: () => setTab('evidence') },
                { label: 'subjects', value: (packet.members || []).filter((m) => m.member_type === 'person').length, go: () => setTab('subjects') },
              ].map((stat) => (
                <Box
                  key={stat.label}
                  onClick={stat.go}
                  sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4, cursor: 'pointer', '&:hover .v': { color: '#f5b942' } }}
                >
                  <Typography className="v" variant="body2" sx={{ fontWeight: 800 }}>{stat.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                </Box>
              ))}
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              onClick={() => setTab('entities')}
              sx={{ display: 'block', mb: 1.25, cursor: 'pointer', '&:hover': { color: 'text.primary' } }}
            >
              {(packet.members || []).length
                ? (packet.members || []).slice(0, 6).map((m) => m.label || m.member_id).join(' · ')
                  + ((packet.members || []).length > 6 ? ` +${(packet.members || []).length - 6} more` : '')
                : 'No roster yet — add a geofence or device'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
              <Typography
                variant="caption"
                onClick={() => (canCollect && !busy ? handleCollect() : undefined)}
                sx={{
                  fontWeight: 700,
                  cursor: canCollect && !busy ? 'pointer' : 'default',
                  color: canCollect && !busy ? '#f5b942' : 'text.disabled',
                }}
              >
                Collect now
              </Typography>
              {draftInsights > 0 && (
                <Typography
                  variant="caption"
                  onClick={() => { setTab('analysis'); setAnalysisSub('insights'); }}
                  sx={{ fontWeight: 700, cursor: 'pointer', color: '#f5b942' }}
                >
                  Review {draftInsights} draft{draftInsights === 1 ? '' : 's'}
                </Typography>
              )}
              <Typography
                variant="caption"
                onClick={() => setTab('mission')}
                sx={{ fontWeight: 700, cursor: 'pointer' }}
              >
                Plan a mission
              </Typography>
              <Typography
                variant="caption"
                onClick={() => exportPacket(packetId, 'html')}
                sx={{ fontWeight: 700, cursor: 'pointer' }}
              >
                Export report
              </Typography>
            </Box>

            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Latest assessment</Typography>
            {latestAssessment ? (
              <Box sx={{ mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'rgba(245,185,66,0.5)' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {latestAssessment.version_label}
                  {` · ${latestAssessment.confidence || packet.confidence || 'confidence unset'}`}
                  {latestAssessment.published_by ? ` · ${latestAssessment.published_by}` : ' · draft'}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {latestAssessment.executive_summary || 'No executive summary.'}
                </Typography>
                <Typography
                  variant="caption"
                  onClick={() => setTab('assessment')}
                  sx={{ cursor: 'pointer', color: '#f5b942', fontWeight: 700 }}
                >
                  Open assessment
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Nothing published yet.{' '}
                <Box
                  component="span"
                  onClick={() => setTab('assessment')}
                  sx={{ cursor: 'pointer', color: '#f5b942', fontWeight: 700 }}
                >
                  Draft one
                </Box>
                {' '}from the file.
              </Typography>
            )}

            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Significant activity</Typography>
            {activity.slice(0, 8).map((ev) => (
              <Box key={ev.id} sx={{ mb: 0.6 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.35 }}>{ev.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {ev.significance} · {ev.source} · {ev.observation_count} obs · {new Date(ev.started_at).toLocaleString()}
                </Typography>
              </Box>
            ))}
            {activity.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {packet.primary_subject_type
                  ? `Nothing yet on this ${packet.primary_subject_type.replace(/_/g, ' ')}. Collectors are ${canCollect ? 'running' : 'off until Collection'}.`
                  : 'No activity yet. Attach a geofence or device, then collect.'}
              </Typography>
            )}

            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Analyst comments</Typography>
            {notes.slice(0, 5).map((n) => (
              <Typography key={n.id} variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                {n.content}
              </Typography>
            ))}
            <TextField
              size="small"
              fullWidth
              placeholder="Add a comment"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key !== 'Enter' || e.shiftKey || !noteDraft.trim() || busy) return;
                e.preventDefault();
                setBusy(true);
                try {
                  await addPacketNote(packetId, { content: noteDraft.trim(), note_type: 'judgment' });
                  setNoteDraft('');
                  setNotes(await listPacketNotes(packetId));
                } finally {
                  setBusy(false);
                }
              }}
              InputProps={{ sx: { fontSize: '0.82rem' } }}
              sx={{ mt: 0.5 }}
            />
          </Box>
        )}

        {tab === 'assessment' && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography
                variant="caption"
                onClick={() => !sentinelBusy && handleSentinelInsights()}
                sx={{ fontWeight: 700, cursor: sentinelBusy ? 'default' : 'pointer', color: '#f5b942' }}
              >
                {sentinelBusy === 'insights' ? 'Drafting insights…' : 'Draft insights'}
              </Typography>
              <Typography
                variant="caption"
                onClick={() => !sentinelBusy && handleSentinelAssessment()}
                sx={{ fontWeight: 700, cursor: sentinelBusy ? 'default' : 'pointer', color: '#f5b942' }}
              >
                {sentinelBusy === 'assessment' ? 'Drafting assessment…' : 'Draft assessment'}
              </Typography>
              <Typography
                variant="caption"
                onClick={() => setComposeOpen(true)}
                sx={{ fontWeight: 700, cursor: 'pointer' }}
              >
                {assessSummary.trim() ? 'Continue writing' : 'Write assessment'}
              </Typography>
              <Typography
                variant="caption"
                onClick={() => exportPacket(packetId, 'html')}
                sx={{ fontWeight: 700, cursor: 'pointer' }}
              >
                Export report
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Sentinel drafts from the packet file. Nothing publishes until you review it.
              {canPublish ? '' : ' Move to Analysis to publish.'}
            </Typography>

            {assessSummary.trim() && (
              <Box sx={{ mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'rgba(245,185,66,0.5)' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Unpublished draft · {assessLabel}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                  {assessSummary}
                </Typography>
                <Typography
                  variant="caption"
                  onClick={() => setComposeOpen(true)}
                  sx={{ cursor: 'pointer', color: '#f5b942', fontWeight: 700 }}
                >
                  Review and publish
                </Typography>
              </Box>
            )}

            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Published history</Typography>
            {assessments.filter((a) => a.published_at).length === 0 && (
              <Typography variant="caption" color="text.secondary">No published versions yet.</Typography>
            )}
            {assessments.filter((a) => a.published_at).map((a) => (
              <Box key={a.id} sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {a.version_label} · {a.published_at ? new Date(a.published_at).toLocaleString() : ''}
                  {a.published_by ? ` · ${a.published_by}` : ''}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                  {a.executive_summary}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {tab === 'analysis' && (
          <Box>
            <Tabs
              value={analysisSub}
              onChange={(_, v) => setAnalysisSub(v)}
              sx={{ minHeight: 28, mb: 1, '& .MuiTab-root': { minHeight: 28, fontSize: '0.7rem', textTransform: 'none' } }}
            >
              <Tab value="activity" label="Activity" />
              <Tab value="pol" label="Pattern of Life" />
              <Tab value="insights" label="Insights" />
            </Tabs>
            {analysisSub === 'activity' && (
              <Box>
                {activity.map((ev) => {
                  const listed = Array.isArray(ev.entity_refs?.devices) ? ev.entity_refs.devices : [];
                  return (
                  <Box key={ev.id} sx={{ mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{ev.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {ev.summary}
                    </Typography>
                    {listed.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {listed.map((raw) => {
                          const p = typeof raw === 'string'
                            ? { id: raw, label: raw, kind: ev.source === 'adtech' ? 'adtech' : 'device', count: 1 }
                            : { id: String((raw as any).id || ''), label: String((raw as any).id || ''), kind: ev.source === 'adtech' ? 'adtech' : 'device', count: Number((raw as any).count) || 1, latitude: (raw as any).latitude, longitude: (raw as any).longitude };
                          if (!p.id) return null;
                          const excluded = excludedKeys.has(p.id);
                          const onPacket = memberKeys.has(p.id) && !excluded;
                          return (
                            <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.15 }}>
                              <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, Menlo, monospace', flex: 1, minWidth: 0 }}>
                                {p.id}{p.count > 1 ? ` · ${p.count} pings` : ''}
                              </Typography>
                              {excluded && <Typography variant="caption" color="text.secondary">Excluded</Typography>}
                              {onPacket && <Typography variant="caption" color="text.secondary">On packet</Typography>}
                              {!onPacket && !excluded && (
                                <>
                                  <Button size="small" sx={{ minWidth: 0, py: 0 }} disabled={addingPlayer === p.id} onClick={() => addPlayerToPacket(p as IntelActivityPlayer)}>
                                    Add
                                  </Button>
                                  <Button size="small" color="inherit" sx={{ minWidth: 0, py: 0 }} disabled={addingPlayer === p.id} onClick={() => excludePlayer(p as IntelActivityPlayer)}>
                                    Exclude
                                  </Button>
                                </>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                    <Button size="small" onClick={() => openObservations(ev.id)}>
                      {obsOpen === ev.id ? 'Hide' : `Drill down (${ev.observation_count})`}
                    </Button>
                    {obsOpen === ev.id && (
                      <Box sx={{ mt: 0.75, pl: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {obsSummary || 'No unique players in this window.'}
                        </Typography>
                        {obsPlayers.map((p) => {
                          const excluded = excludedKeys.has(p.id) || excludedKeys.has(p.label);
                          const onPacket = (memberKeys.has(p.id) || memberKeys.has(p.label)) && !excluded;
                          const canAdd = !onPacket && !excluded && !!playerMemberType(p);
                          return (
                            <Box key={p.id} sx={{ mb: 0.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                  {p.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {p.kind}
                                  {p.count ? ` · ${p.count} hit${p.count === 1 ? '' : 's'}` : ''}
                                  {p.detail ? ` · ${p.detail}` : ''}
                                  {p.last_at ? ` · last ${new Date(p.last_at).toLocaleString()}` : ''}
                                  {p.latitude != null && p.longitude != null
                                    ? ` · ${Number(p.latitude).toFixed(4)}, ${Number(p.longitude).toFixed(4)}`
                                    : ''}
                                </Typography>
                              </Box>
                              {excluded && (
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', pt: 0.25 }}>
                                  Excluded
                                </Typography>
                              )}
                              {onPacket && (
                                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', pt: 0.25 }}>
                                  On packet
                                </Typography>
                              )}
                              {canAdd && (
                                <>
                                  <Button
                                    size="small"
                                    disabled={addingPlayer === p.id}
                                    onClick={() => addPlayerToPacket(p)}
                                    sx={{ minWidth: 0, py: 0 }}
                                  >
                                    Add
                                  </Button>
                                  <Button
                                    size="small"
                                    color="inherit"
                                    disabled={addingPlayer === p.id}
                                    onClick={() => excludePlayer(p)}
                                    sx={{ minWidth: 0, py: 0 }}
                                  >
                                    Exclude
                                  </Button>
                                </>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                  );
                })}
                {activity.length === 0 && <Typography variant="caption" color="text.secondary">No grouped activity yet. Enable monitors and hit Play.</Typography>}
              </Box>
            )}
            {analysisSub === 'pol' && (
              <Box>
                {insights.filter((i) => i.category === 'behavioral' || i.origin === 'system').map((i) => (
                  <Box key={i.id} sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{i.title}</Typography>
                    <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', display: 'block' }}>{i.finding}</Typography>
                    <Typography variant="caption" color="text.secondary">{i.validation}</Typography>
                  </Box>
                ))}
                {insights.filter((i) => i.category === 'behavioral' || i.origin === 'system').length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Enable Pattern of Life and collect. Dense GPS uses Dwell Analysis stay-points. AdTech and watchlist IDs without dwells use return-to-place (same ~150m cell on 2+ days) — one ping is a visit, not a linger.
                  </Typography>
                )}
              </Box>
            )}
            {analysisSub === 'insights' && (
              <Box>
                {insights.map((i) => (
                  <Box key={i.id} sx={{ mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{i.title}</Typography>
                    <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', display: 'block' }}>{i.finding}</Typography>
                    <Typography variant="caption" color="text.secondary">{i.origin} · {i.validation}</Typography>
                    {i.validation === 'draft' && (
                      <Box sx={{ mt: 0.5 }}>
                        <Button size="small" onClick={async () => { await validateInsight(packetId, i.id, 'accepted'); setInsights(await listInsights(packetId)); }}>Accept</Button>
                        <Button size="small" color="warning" onClick={async () => { await validateInsight(packetId, i.id, 'rejected'); setInsights(await listInsights(packetId)); }}>Reject</Button>
                      </Box>
                    )}
                  </Box>
                ))}
                <Divider sx={{ my: 1 }} />
                <TextField size="small" fullWidth label="Insight title" value={insightTitle} onChange={(e) => setInsightTitle(e.target.value)} sx={{ mb: 1 }} />
                <TextField size="small" fullWidth multiline minRows={2} label="Finding" value={insightFinding} onChange={(e) => setInsightFinding(e.target.value)} />
                <Button
                  size="small"
                  sx={{ mt: 0.5 }}
                  disabled={!insightTitle.trim() || !insightFinding.trim()}
                  onClick={async () => {
                    await createInsight(packetId, { title: insightTitle.trim(), finding: insightFinding.trim(), origin: 'analyst' });
                    setInsightTitle('');
                    setInsightFinding('');
                    setInsights(await listInsights(packetId));
                  }}
                >
                  Add insight
                </Button>
              </Box>
            )}
          </Box>
        )}

        {tab === 'map' && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Overlays are on the map to the right (roster always on; Activity / PoL / Insights swap with the Analysis tab). This list jumps to a member or a new AOI.
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Roster</Typography>
            {(mapLayers?.roster || []).map((pt, idx) => {
              const lat = Number(pt.latitude);
              const lng = Number(pt.longitude);
              return (
                <Box key={`roster-${idx}`} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2">{pt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {pt.kind || 'member'}
                      {Number.isFinite(lat) && Number.isFinite(lng) ? ` · ${lat.toFixed(4)}, ${lng.toFixed(4)}` : ''}
                    </Typography>
                  </Box>
                  {Number.isFinite(lat) && Number.isFinite(lng) && onZoomTo && (
                    <Button size="small" onClick={() => onZoomTo(lat, lng)}>Zoom</Button>
                  )}
                </Box>
              );
            })}
            {(packet.members || []).filter((m) => !memberExcluded(m) && !(mapLayers?.roster || []).some((pt) => pt.entityId === m.member_id)).map((m) => (
              <Box key={m.id} sx={{ mb: 0.5 }}>
                <Typography variant="body2">{m.label || m.member_id}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {m.member_type === 'overwatch_article' ? 'OSINT' : m.member_type}
                  {m.is_primary ? ' · primary' : ''}
                  {' · no map point yet'}
                </Typography>
              </Box>
            ))}
            {(packet.members || []).length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Add a device or promote a geofence to populate the map.
              </Typography>
            )}
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1.5, mb: 0.5 }}>New AOIs</Typography>
            {(mapLayers?.insights || []).map((pt, idx) => {
              const lat = Number(pt.latitude);
              const lng = Number(pt.longitude);
              return (
                <Box key={`aoi-${idx}`} sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2">{pt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(4)}, ${lng.toFixed(4)}` : 'new area'}
                    </Typography>
                  </Box>
                  {Number.isFinite(lat) && Number.isFinite(lng) && onZoomTo && (
                    <Button size="small" onClick={() => onZoomTo(lat, lng)}>Zoom</Button>
                  )}
                </Box>
              );
            })}
            {!(mapLayers?.insights || []).length && (
              <Typography variant="caption" color="text.secondary">
                Historic PoL will list recurring places outside the packet fence here.
              </Typography>
            )}
          </Box>
        )}

        {tab === 'research' && (
          <Box>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder="Ask Tavily about this packet"
              value={researchQuery}
              onChange={(e) => setResearchQuery(e.target.value)}
            />
            <Button
              size="small"
              sx={{ mt: 0.5, mr: 0.5 }}
              disabled={!researchQuery.trim() || busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const res = await research(packetId, researchQuery.trim());
                  setResearchResult({ summary: res.summary, sources: res.sources || [] });
                  if (!res.success) setError(res.error || 'Research failed');
                } finally {
                  setBusy(false);
                }
              }}
            >
              Search
            </Button>
            {researchResult && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{researchResult.summary}</Typography>
                {researchResult.sources.map((s, i) => (
                  <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                    {s.title} — {s.url}
                  </Typography>
                ))}
                <Button
                  size="small"
                  onClick={async () => {
                    await research(packetId, researchQuery.trim(), 'evidence');
                    setEvidence(await listEvidence(packetId));
                  }}
                >
                  Save as evidence
                </Button>
                <Button
                  size="small"
                  onClick={async () => {
                    await research(packetId, researchQuery.trim(), 'insight');
                    setInsights(await listInsights(packetId));
                  }}
                >
                  Save as draft insight
                </Button>
              </Box>
            )}
          </Box>
        )}

        {tab === 'evidence' && (
          <Box>
            {evidence.map((e) => (
              <EvidenceCard
                key={e.id}
                evidence={e}
                busy={busy}
                onZoomTo={onZoomTo}
                onDelete={async () => {
                  if (!window.confirm(`Delete evidence “${e.title}”?`)) return;
                  setBusy(true);
                  try {
                    await deleteEvidence(packetId, e.id);
                    setEvidence(await listEvidence(packetId));
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            ))}
            {evidence.length === 0 && <Typography variant="caption" color="text.secondary">No evidence yet.</Typography>}
            <Divider sx={{ my: 1 }} />
            <Button
              size="small"
              onClick={async () => {
                const title = window.prompt('Evidence title');
                const body = title ? window.prompt('Evidence text') : null;
                if (!title || !body) return;
                await addEvidence(packetId, { title, body, kind: 'note', source: 'analyst' });
                setEvidence(await listEvidence(packetId));
              }}
            >
              Add evidence note
            </Button>
          </Box>
        )}

        {tab === 'subjects' && (
          <SubjectDossierPanel packetId={packetId} onError={setError} />
        )}

        {tab === 'entities' && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Added IDs are watched (PoL uses all of their locations, not just this fence). Excluded IDs stay off discovery lists.
            </Typography>
            {(packet.members || []).length === 0 && (
              <Typography variant="caption" color="text.secondary">
                Add a device or AdTech ID from Activity, or promote a geofence, to build this roster.
              </Typography>
            )}
            {(packet.members || []).map((m) => (
              <EntityRosterCard
                key={m.id}
                member={m}
                busy={busy}
                onSave={async (patch) => {
                  setBusy(true);
                  try {
                    await refreshRoster(await updateMember(packetId, m.id, patch));
                  } finally {
                    setBusy(false);
                  }
                }}
                onRemove={async () => {
                  if (m.is_primary) return;
                  setBusy(true);
                  try {
                    await refreshRoster(await removeMember(packetId, m.id));
                  } finally {
                    setBusy(false);
                  }
                }}
                onHistoric={async () => {
                  setBusy(true);
                  try {
                    const hist = await runHistoricPol(packetId, m.id);
                    await refreshRoster(hist.packet);
                    setActivity(await listActivity(packetId));
                    setInsights(await listInsights(packetId));
                    setTab('analysis');
                    setAnalysisSub('pol');
                    const pt = hist.snapshot?.new_aois?.[0] || hist.snapshot?.places?.[0];
                    if (pt && onZoomTo && Number.isFinite(pt.latitude) && Number.isFinite(pt.longitude)) {
                      onZoomTo(pt.latitude, pt.longitude, 14);
                    }
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            ))}
          </Box>
        )}

        {tab === 'mission' && (
          <MissionWorkspace
            packetId={packetId}
            defaultCenter={packet.focus ? {
              latitude: packet.focus.latitude,
              longitude: packet.focus.longitude,
              label: packet.focus.label,
            } : null}
            onError={setError}
            onWaypointsChange={(mission) => {
              onLoadPacketLayer?.(
                `intel-packet-${packetId}-mission`,
                (mission?.waypoints || []).map((w, i) => ({
                  latitude: w.latitude,
                  longitude: w.longitude,
                  label: sequentialWaypointLabel(w.label, i),
                  entityId: `mission-${w.mission_id}-${w.id}`,
                  kind: 'mission',
                })),
                mission?.route_geometry || null,
              );
            }}
            onWaypointPickChange={onWaypointPickChange}
            waypointPickRef={waypointPickRef}
          />
        )}

        {tab === 'admin' && (
          <Box>
            <Tabs
              value={adminSub}
              onChange={(_, v) => setAdminSub(v)}
              sx={{ minHeight: 28, mb: 1, '& .MuiTab-root': { minHeight: 28, fontSize: '0.7rem', textTransform: 'none' } }}
            >
              <Tab value="monitoring" label="Monitoring" />
              <Tab value="audit" label="Audit Log" />
              <Tab value="access" label="Access" />
            </Tabs>
            {adminSub === 'monitoring' && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                  Toggle to arm. Click a row for what it does and its settings. Enabled monitors pull every 15 minutes while status is Collection, Analysis, Published, or Monitoring.
                </Typography>
                {(packet.monitors || []).filter((m) => WIRED_MONITOR_KINDS.includes(m.kind as MonitorKind)).map((m) => (
                  <Box
                      key={m.kind}
                      onClick={() => setMonitorInfoKind(m.kind as MonitorKind)}
                      sx={{
                        mb: 1,
                        px: 1.25,
                        py: 1,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: m.enabled ? 'rgba(229,57,53,0.35)' : 'divider',
                        bgcolor: m.enabled ? 'rgba(229,57,53,0.06)' : 'transparent',
                        cursor: 'pointer',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <Switch
                          size="small"
                          checked={!!m.enabled}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleMonitor(m)}
                          sx={{ mt: 0.25 }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {MONITOR_LABELS[m.kind] || m.kind}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {m.last_run_at ? `Last run ${new Date(m.last_run_at).toLocaleString()}` : 'Never run'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                ))}
                <Button size="small" startIcon={<PlayArrowIcon />} onClick={handleCollect} disabled={busy || !canCollect}>Collect now</Button>
                <MonitorInfoDialog
                  open={!!infoMonitor}
                  monitor={infoMonitor}
                  bufferMeters={infoMonitor ? monitorBuffer(infoMonitor) : 0}
                  onClose={() => setMonitorInfoKind(null)}
                  onBufferChange={
                    infoMonitor && BUFFER_KINDS.has(infoMonitor.kind)
                      ? (v) => setBufferDraft((prev) => ({ ...prev, [infoMonitor.kind]: v }))
                      : undefined
                  }
                  onBufferCommit={
                    infoMonitor && BUFFER_KINDS.has(infoMonitor.kind)
                      ? (v) => commitBuffer(infoMonitor, v)
                      : undefined
                  }
                  onEditNewsTerms={() => setNewsQueryOpen(true)}
                  onClearNewsTerms={() => applyNewsQuery({ and: '', or: '', not: '', phrase: '' })}
                  formatBuffer={formatBuffer}
                />
                <NewsQueryBuilderDialog
                  open={newsQueryOpen}
                  initial={parseNewsQuery((packet.monitors || []).find((x) => x.kind === 'news')?.thresholds?.news_query)}
                  onClose={() => setNewsQueryOpen(false)}
                  onApply={applyNewsQuery}
                />
              </Box>
            )}
            {adminSub === 'audit' && (
              <Box>
                {audit.map((a) => (
                  <Typography key={a.id} variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                    {new Date(a.created_at).toLocaleString()} · {a.actor_kind}/{a.actor} · {a.action}
                  </Typography>
                ))}
                {audit.length === 0 && <Typography variant="caption" color="text.secondary">No audit events.</Typography>}
              </Box>
            )}
            {adminSub === 'access' && (
              <Box>
                <TextField
                  size="small"
                  fullWidth
                  label="Assigned analyst"
                  defaultValue={packet.assigned_analyst || ''}
                  onBlur={async (e) => {
                    setPacket(await updatePacket(packetId, { assigned_analyst: e.target.value || undefined }));
                  }}
                  sx={{ mb: 1 }}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Course ID"
                  defaultValue={packet.course_id || ''}
                  onBlur={async (e) => {
                    setPacket(await updatePacket(packetId, { course_id: e.target.value || undefined }));
                  }}
                  sx={{ mb: 1.5 }}
                />
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  Import unfiled intel notes
                </Typography>
                <Button
                  size="small"
                  onClick={async () => setUnfiled(await listUnfiledNotes())}
                >
                  Load unfiled notes
                </Button>
                {unfiled.map((n) => (
                  <FormControlLabel
                    key={n.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedUnfiled.has(n.id)}
                        onChange={(e) => {
                          setSelectedUnfiled((prev) => {
                            const next = new Set(prev);
                            e.target.checked ? next.add(n.id) : next.delete(n.id);
                            return next;
                          });
                        }}
                      />
                    }
                    label={<Typography variant="caption">{n.title || `Note ${n.id}`}</Typography>}
                    sx={{ display: 'flex' }}
                  />
                ))}
                {unfiled.length > 0 && (
                  <Button
                    size="small"
                    disabled={selectedUnfiled.size === 0}
                    onClick={async () => {
                      await importNotes(packetId, Array.from(selectedUnfiled));
                      setSelectedUnfiled(new Set());
                      setEvidence(await listEvidence(packetId));
                    }}
                  >
                    Import selected into Evidence
                  </Button>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
      <AddMemberDialog
        open={!!addDraft}
        draft={addDraft}
        busy={!!addingPlayer}
        onClose={() => setAddDraft(null)}
        onConfirm={confirmAddMember}
      />
      <Dialog open={composeOpen} onClose={() => !busy && setComposeOpen(false)} maxWidth="md" fullWidth>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, flex: 1 }}>Assessment · {packet.name}</Typography>
          <Select
            variant="standard"
            disableUnderline
            value={assessLabel}
            onChange={(e) => setAssessLabel(e.target.value)}
            sx={{ fontSize: '0.78rem', fontWeight: 700, mr: 0.5 }}
          >
            {['Initial', '24h', '72h', 'Final'].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </Select>
          <IconButton size="small" onClick={() => setComposeOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </Box>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <TextField size="small" fullWidth multiline minRows={3} label="Executive summary" value={assessSummary} onChange={(e) => setAssessSummary(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField size="small" fullWidth multiline minRows={3} label="Key findings" value={assessFindings} onChange={(e) => setAssessFindings(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField size="small" fullWidth multiline minRows={2} label="Pattern of Life" value={assessPol} onChange={(e) => setAssessPol(e.target.value)} sx={{ mb: 1.5 }} />
          <TextField size="small" fullWidth multiline minRows={2} label="Gaps" value={assessGaps} onChange={(e) => setAssessGaps(e.target.value)} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
            <Typography
              variant="caption"
              onClick={async () => {
                if (!assessSummary.trim() || busy || !canPublish) return;
                setBusy(true);
                setError(null);
                try {
                  await createAssessment(packetId, {
                    version_label: assessLabel,
                    executive_summary: assessSummary,
                    key_findings: assessFindings,
                    pol_assessment: assessPol,
                    gaps: assessGaps,
                    publish: true,
                  });
                  setAssessSummary('');
                  setAssessFindings('');
                  setAssessPol('');
                  setAssessGaps('');
                  await loadHeader();
                  setAssessments(await listAssessments(packetId));
                  setComposeOpen(false);
                  setCollectNote('Assessment published. Move to Monitoring to keep watching for changes.');
                } catch (e: any) {
                  setError(e?.response?.data?.detail || e?.message || 'Publish failed');
                } finally {
                  setBusy(false);
                }
              }}
              sx={{
                fontWeight: 700,
                cursor: assessSummary.trim() && canPublish && !busy ? 'pointer' : 'default',
                color: assessSummary.trim() && canPublish && !busy ? '#f5b942' : 'text.disabled',
              }}
            >
              Publish
            </Typography>
            <Typography
              variant="caption"
              onClick={() => setComposeOpen(false)}
              sx={{ cursor: 'pointer', color: 'text.secondary' }}
            >
              Keep as draft
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: 'right' }}>
              {canPublish
                ? 'Publishing writes a versioned product and sets status to Published.'
                : 'Move to Analysis to publish.'}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const KEYCAP_OCR = new Set([
  'esc', 'escape', 'tab', 'caps', 'capslock', 'shift', 'ctrl', 'control', 'alt',
  'fn', 'win', 'windows', 'backspace', 'delete', 'enter', 'return', 'space',
  'spacebar', 'insert', 'home', 'end', 'pgup', 'pgdn', 'pageup', 'pagedown',
]);
for (let i = 1; i <= 24; i += 1) KEYCAP_OCR.add(`f${i}`);

function isKeycapOcr(line: string): boolean {
  const t = line.trim().toLowerCase();
  if (!t) return true;
  if (KEYCAP_OCR.has(t)) return true;
  return t.length <= 2 && /^[a-z0-9]+$/i.test(t.replace(/\s/g, ''));
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object' && 'label' in item) return String((item as { label?: string }).label || '').trim();
      if (item && typeof item === 'object' && 'name' in item) return String((item as { name?: string }).name || '').trim();
      return '';
    })
    .filter(Boolean);
}

function notesFromBody(body?: string | null): string {
  if (!body) return '';
  const cut = body.search(/\n\n(?:Caption:|OCR:|Faces detected:|Possible face|Face pipeline:)/);
  return (cut >= 0 ? body.slice(0, cut) : body).trim();
}

function readEvidenceMeta(evidence: IntelEvidence): Record<string, unknown> {
  const raw = evidence.meta as unknown;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

function EvidenceCard({
  evidence,
  busy,
  onDelete,
  onZoomTo,
}: {
  evidence: IntelEvidence;
  busy: boolean;
  onDelete: () => void;
  onZoomTo?: (lat: number, lng: number, zoom?: number) => void;
}) {
  const meta = readEvidenceMeta(evidence);
  const reportId = String(meta.field_report_id || '');
  const hasPhoto = Boolean(meta.has_photo) || Boolean(reportId);
  const caption = String(meta.caption || '') || (evidence.body?.match(/^Caption:\s*(.+)$/m)?.[1] || '');
  const scene = String(meta.scene || caption);
  const objects = asStringList(meta.objects);
  const tags = asStringList(meta.tags);
  const rawOcr = String(meta.ocr_text || '') || (evidence.body?.split('OCR:\n')[1] || '');
  const ocrLines = rawOcr
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l && !isKeycapOcr(l) && !/^(Faces detected:|Possible face|Face pipeline:|Caption:)/.test(l));
  const faceMatches = asStringList(meta.face_matches);
  const faceCount = typeof meta.face_count === 'number' ? meta.face_count : 0;
  const lat = typeof meta.latitude === 'number' ? meta.latitude : Number(meta.latitude);
  const lon = typeof meta.longitude === 'number' ? meta.longitude : Number(meta.longitude);
  const hasLoc = Number.isFinite(lat) && Number.isFinite(lon);
  const acc = typeof meta.accuracy_meters === 'number' ? meta.accuracy_meters : null;
  const notes = notesFromBody(evidence.body);
  const collected = evidence.collected_at || evidence.created_at;

  return (
    <Box sx={{ mb: 1.25, p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{evidence.title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {evidence.kind} · {evidence.source || '—'}
            {collected ? ` · ${new Date(collected).toLocaleString()}` : ''}
          </Typography>
        </Box>
        <Tooltip title="Delete evidence">
          <span>
            <IconButton size="small" onClick={onDelete} disabled={busy} aria-label="Delete evidence">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {notes && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, whiteSpace: 'pre-wrap' }}>
          {notes}
        </Typography>
      )}
      {typeof meta.enrichment_error === 'string' && meta.enrichment_error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
          Identification failed: {String(meta.enrichment_error).slice(0, 180)}
        </Typography>
      )}
      {Boolean(meta.enrichment_pending) && !scene && !objects.length && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          Identification in progress — refresh in a moment.
        </Typography>
      )}
      {!scene && !objects.length && !ocrLines.length && hasPhoto && !meta.enrichment_error && !meta.enrichment_pending && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          No scene, objects, or text returned for this photo.
        </Typography>
      )}
      {scene && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75 }}>
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 700 }}>Scene </Box>
          {scene}
        </Typography>
      )}
      {objects.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          Detected: {objects.slice(0, 10).join(', ')}
        </Typography>
      )}
      {tags.length > 0 && objects.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {tags.slice(0, 8).join(' · ')}
        </Typography>
      )}
      {(faceCount > 0 || faceMatches.length > 0) && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.75 }}>
          <Box component="span" sx={{ color: 'text.secondary', fontWeight: 700 }}>Faces </Box>
          {faceCount || faceMatches.length}
          {faceMatches.length > 0 ? ` · ${faceMatches.join('; ')}` : ''}
        </Typography>
      )}
      {hasLoc && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
          <PlaceIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography variant="caption">
            {lat.toFixed(5)}, {lon.toFixed(5)}
            {acc != null && acc >= 0 ? ` · ±${Math.round(acc)} m` : ''}
          </Typography>
          {onZoomTo && (
            <Button size="small" sx={{ minWidth: 0, py: 0, fontSize: '0.7rem' }} onClick={() => onZoomTo(lat, lon, 16)}>
              Map
            </Button>
          )}
        </Box>
      )}
      {ocrLines.length > 0 && (
        <Box sx={{ mt: 0.75 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>Text in image</Typography>
          {ocrLines.slice(0, 12).map((line) => (
            <Typography key={line} variant="caption" sx={{ display: 'block' }}>{line}</Typography>
          ))}
        </Box>
      )}
      {hasPhoto && reportId && <FieldReportEvidencePhotos reportId={reportId} />}
    </Box>
  );
}

function FieldReportEvidencePhotos({ reportId }: { reportId: string }) {
  const [original, setOriginal] = useState<string | null>(null);
  const [annotated, setAnnotated] = useState<string | null>(null);
  const [open, setOpen] = useState<'original' | 'annotated' | null>(null);
  const [rotation, setRotation] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const orig = await fetchFieldReportPhoto(reportId, 'original');
        if (alive) setOriginal(orig);
      } catch {
        /* original may be missing */
      }
      try {
        const ann = await fetchFieldReportPhoto(reportId, 'annotated');
        if (alive) setAnnotated(ann);
      } catch {
        /* annotated only exists after DataHog enrichment */
      }
    })();
    return () => {
      alive = false;
    };
  }, [reportId]);

  const preview = open === 'annotated' ? annotated : original;
  const openPreview = (which: 'original' | 'annotated') => {
    setRotation(0);
    setOpen(which);
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        {original && (
          <Box>
            <Typography variant="caption" color="text.secondary">Original</Typography>
            <Box
              component="img"
              src={original}
              alt="Field report original"
              onClick={() => openPreview('original')}
              sx={{
                display: 'block', maxWidth: 240, maxHeight: 180, borderRadius: 1,
                cursor: 'zoom-in', border: '1px solid', borderColor: 'divider',
              }}
            />
          </Box>
        )}
        {annotated && (
          <Box>
            <Typography variant="caption" color="text.secondary">Annotated</Typography>
            <Box
              component="img"
              src={annotated}
              alt="Field report annotated"
              onClick={() => openPreview('annotated')}
              sx={{
                display: 'block', maxWidth: 240, maxHeight: 180, borderRadius: 1,
                cursor: 'zoom-in', border: '1px solid', borderColor: 'divider',
              }}
            />
          </Box>
        )}
      </Box>
      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1, py: 1 }}>
          <ZoomInIcon fontSize="small" />
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {open === 'annotated' ? 'Annotated' : 'Original'}
          </Typography>
          {annotated && original && (
            <Button size="small" onClick={() => setOpen(open === 'annotated' ? 'original' : 'annotated')}>
              {open === 'annotated' ? 'Show original' : 'Show annotated'}
            </Button>
          )}
          <Tooltip title="Rotate">
            <IconButton size="small" onClick={() => setRotation((r) => (r + 90) % 360)} aria-label="Rotate">
              <RotateRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => setOpen(null)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.35)', minHeight: 240 }}>
          {preview && (
            <Box
              component="img"
              src={preview}
              alt="Field report enlarged"
              sx={{
                maxWidth: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                transform: `rotate(${rotation}deg)`,
                transition: 'transform 0.15s ease',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default IntelPacketWorkspace;
